<template>
  <v-dialog v-model="show" max-width="500" persistent>
    <v-card>
      <v-card-title class="headline error white--text d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
        {{ title || 'Confirm Delete' }}
      </v-card-title>
      
      <v-card-text class="pt-4">
        <div class="text-center">
          <v-icon size="64" color="error" class="mb-3">mdi-delete-alert</v-icon>
          <h3 class="mb-3">{{ message || 'Are you sure you want to delete this item?' }}</h3>
          
          <!-- Item Info Display -->
          <div v-if="item" class="item-info-card pa-3 ma-2" style="background: #f5f5f5; border-radius: 8px;">
            <div class="d-flex align-center justify-center">
              <v-avatar size="48" class="mr-3" :color="avatarColor || 'primary'">
                <v-icon color="white" size="24">{{ avatarIcon || 'mdi-folder' }}</v-icon>
              </v-avatar>
              <div class="text-left">
                <div class="font-weight-bold text-body-1">
                  {{ getItemDisplayName() }}
                </div>
                <div v-if="getItemDisplayDescription()" class="text-caption grey--text">
                  {{ getItemDisplayDescription() }}
                </div>
                <div v-if="showItemStats" class="text-caption grey--text">
                  {{ getItemStats() }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- Warning Messages -->
          <div class="warning-section mt-3">
            <p class="text-body-2 grey--text mb-1">
              <strong>Warning:</strong> {{ warningMessage || 'This action cannot be undone.' }}
            </p>
            <p v-if="additionalInfo" class="text-body-2 grey--text">
              {{ additionalInfo }}
            </p>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="px-4 pb-4">
        <v-spacer></v-spacer>
        <v-btn
          color="grey darken-1"
          text
          @click="cancel"
          :disabled="loading"
        >
          <v-icon left>mdi-close</v-icon>
          {{ cancelText || 'Cancel' }}
        </v-btn>
        <v-btn
          color="error"
          @click="confirm"
          :loading="loading"
          :disabled="loading"
        >
          <v-icon left>mdi-delete</v-icon>
          {{ confirmText || 'Delete' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "DeleteConfirmationDialog",
  
  props: {
    // Dialog visibility
    value: {
      type: Boolean,
      default: false
    },
    
    // Dialog content
    title: {
      type: String,
      default: 'Confirm Delete'
    },
    message: {
      type: String,
      default: 'Are you sure you want to delete this item?'
    },
    warningMessage: {
      type: String,
      default: 'This action cannot be undone.'
    },
    additionalInfo: {
      type: String,
      default: null
    },
    
    // Button texts
    cancelText: {
      type: String,
      default: 'Cancel'
    },
    confirmText: {
      type: String,
      default: 'Delete'
    },
    
    // Item to display (new API)
    item: {
      type: Object,
      default: null
    },
    
    // Item ID for backward compatibility
    itemId: {
      type: [String, Number],
      default: null
    },
    
    // Item display configuration
    itemNameField: {
      type: String,
      default: 'name'
    },
    itemDescriptionField: {
      type: String,
      default: 'description'
    },
    
    // Avatar configuration
    avatarIcon: {
      type: String,
      default: 'mdi-folder'
    },
    avatarColor: {
      type: String,
      default: 'primary'
    },
    
    // Additional features
    showItemStats: {
      type: Boolean,
      default: false
    },
    
    // Loading state
    loading: {
      type: Boolean,
      default: false
    }
  },
  
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    }
  },
  
  methods: {
    // Method expected by existing code
    openDialog() {
      this.show = true;
    },
    
    // Method to close dialog programmatically
    closeDialog() {
      this.show = false;
    },
    
    getItemDisplayName() {
      const itemData = this.item || {};
      
      if (!this.item && this.itemId) {
        return `Item ID: ${this.itemId}`;
      }
      
      if (!this.item) return 'Unknown Item';
      
      // Try multiple possible name fields
      const nameFields = [this.itemNameField, 'name', 'title', 'label', 'description'];
      
      for (const field of nameFields) {
        if (itemData[field]) {
          return itemData[field];
        }
      }
      
      return 'Unknown Item';
    },
    
    getItemDisplayDescription() {
      if (!this.item) return null;
      
      // Try description field if it's different from name field
      if (this.itemDescriptionField !== this.itemNameField && this.item[this.itemDescriptionField]) {
        return this.item[this.itemDescriptionField];
      }
      
      // Try other common description fields
      const descFields = ['description', 'subtitle', 'notes', 'details'];
      
      for (const field of descFields) {
        if (field !== this.itemNameField && this.item[field]) {
          return this.item[field];
        }
      }
      
      return null;
    },
    
    getItemStats() {
      if (!this.item || !this.showItemStats) return null;
      
      const stats = [];
      
      // Common stat fields
      if (this.item.created_at) {
        const date = new Date(this.item.created_at);
        stats.push(`Created: ${date.toLocaleDateString()}`);
      }
      
      if (this.item.is_active !== undefined) {
        stats.push(`Status: ${this.item.is_active ? 'Active' : 'Inactive'}`);
      }
      
      if (this.item.count !== undefined) {
        stats.push(`Count: ${this.item.count}`);
      }
      
      return stats.join(' • ');
    },
    
    cancel() {
      this.show = false;
      this.$emit('cancel');
    },
    
    confirm() {
      // Emit both events for backward compatibility
      this.$emit('confirm', this.item || this.itemId);
      this.$emit('delete', this.itemId || this.item?.id);
    }
  }
};
</script>

<style scoped>
/* Item info card styling */
.item-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.item-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Warning section styling */
.warning-section {
  max-width: 400px;
  margin: 0 auto;
}

/* Responsive design */
@media (max-width: 768px) {
  .item-info-card {
    padding: 12px !important;
  }
  
  .item-info-card .d-flex {
    flex-direction: column;
    text-align: center;
  }
  
  .item-info-card .v-avatar {
    margin: 0 0 8px 0 !important;
  }
}
</style>